import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  cn,
} from '@safe-minds-ai/ui';
import { getFlagModerationSubcategoryLabels, getFlagUiCategory, type UiFlagCategory } from '@safe-minds-ai/utils';
import { Flag, RefreshCcw } from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { fetchFlags, reviewFlag } from '../api';
import { useToast } from '../toast';

const PAGE_SIZE = 20;

interface FlagItem {
  id: string;
  parentId: string;
  childId: string;
  sessionId?: string | null;
  messageId?: string | null;
  text: string;
  reason?: string | null;
  tags?: string[] | null;
  parentLabel?: 'CHEATING' | 'NOT_CHEATING' | null;
  systemLabel?: 'CHEATING' | 'NOT_CHEATING' | null;
  finalLabel?: 'CHEATING' | 'NOT_CHEATING' | null;
  parentSafetyLabel?: 'SAFETY' | 'NOT_SAFETY' | null;
  systemSafetyLabel?: 'SAFETY' | 'NOT_SAFETY' | null;
  finalSafetyLabel?: 'SAFETY' | 'NOT_SAFETY' | null;
  parentSafetyType?: 'GENERAL_SAFETY' | 'MENTAL_HEALTH' | 'EXPLICIT_CONTENT' | null;
  systemSafetyType?: 'GENERAL_SAFETY' | 'MENTAL_HEALTH' | 'EXPLICIT_CONTENT' | null;
  finalSafetyType?: 'GENERAL_SAFETY' | 'MENTAL_HEALTH' | 'EXPLICIT_CONTENT' | null;
  modCategories?: Record<string, unknown> | null;
  finalCategory?: Record<string, unknown> | null;
  metadata?: {
    predictedIntent?: { intent?: string | null } | null;
    predictedCheating?: { cheatingRisk?: string | null; requestType?: string | null } | null;
    routerDecision?: { responseMode?: string | null } | null;
    reviewedIntent?: string | null;
    reviewedCheating?: { risk?: string | null; type?: string | null } | null;
    reviewedResponseMode?: string | null;
    reviewedNote?: string | null;
  } | null;
  type: 'MODERATION' | 'CHEATING';
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  createdAt: string;
}

const deriveFlagType = (flag: {
  tags?: string[] | null;
  parentLabel?: 'CHEATING' | 'NOT_CHEATING' | null;
  systemLabel?: 'CHEATING' | 'NOT_CHEATING' | null;
  finalLabel?: 'CHEATING' | 'NOT_CHEATING' | null;
  parentSafetyLabel?: 'SAFETY' | 'NOT_SAFETY' | null;
  systemSafetyLabel?: 'SAFETY' | 'NOT_SAFETY' | null;
  finalSafetyLabel?: 'SAFETY' | 'NOT_SAFETY' | null;
  parentSafetyType?: 'GENERAL_SAFETY' | 'MENTAL_HEALTH' | 'EXPLICIT_CONTENT' | null;
  systemSafetyType?: 'GENERAL_SAFETY' | 'MENTAL_HEALTH' | 'EXPLICIT_CONTENT' | null;
  finalSafetyType?: 'GENERAL_SAFETY' | 'MENTAL_HEALTH' | 'EXPLICIT_CONTENT' | null;
  modCategories?: Record<string, unknown> | null;
  finalCategory?: Record<string, unknown> | null;
}): 'CHEATING' | 'MODERATION' => {
  return getFlagUiCategory(flag) === 'Cheating' ? 'CHEATING' : 'MODERATION';
};

const MODERATION_CATEGORY_OPTIONS = [
  { key: 'harassment', label: 'Harassment' },
  { key: 'hate', label: 'Hate' },
  { key: 'self-harm', label: 'Self-harm' },
  { key: 'sexual', label: 'Sexual content' },
  { key: 'violence', label: 'Violence' },
  { key: 'other', label: 'Other safety concern' },
] as const;

const SAFETY_TYPE_OPTIONS = [
  { value: 'GENERAL_SAFETY', label: 'Safety' },
  { value: 'MENTAL_HEALTH', label: 'Mental Health' },
  { value: 'EXPLICIT_CONTENT', label: 'Explicit Content' },
] as const;

const INTENT_LABELS: Record<string, string> = {
  factual_definition: 'Factual question',
  learning_help: 'Learning help',
  assignment_completion: 'Assignment shortcut',
  sensitive_personal: 'Sensitive personal topic',
  other: 'Other',
};

const CHEATING_TYPE_LABELS: Record<string, string> = {
  FACTUAL: 'Factual question',
  LEARNING_HELP: 'Learning help',
  CHECK_WORK: 'Check work',
  SINGLE_PROBLEM_HELP: 'Single problem help',
  BULK_WORKSHEET: 'Worksheet answers',
  ANSWERS_ONLY: 'Answers only',
  ESSAY_WRITE: 'Write the essay',
  OTHER: 'Other',
};

const RESPONSE_MODE_LABELS: Record<string, string> = {
  direct_answer: 'Direct answer',
  teach_with_example: 'Teach with example',
  check_work_only: 'Check work only',
  coach_refusal: 'Refuse shortcut and coach',
  guided_answer: 'Safety-limited answer',
  safety_redirect: 'Support and escalate',
  refuse_and_escalate: 'Block and redirect',
};

const REASON_LABELS: Record<string, string> = {
  integrity_high: 'Cheating review',
  safety_red: 'High-risk safety',
  safety_yellow: 'Safety-limited response',
  router_review: 'Needs routing review',
  moderation_block: 'Blocked by moderation',
};

const formatLabel = (value?: string | null) => {
  if (!value) return null;
  return value
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const getFlagHeadline = (flag: FlagItem) => {
  if (flag.type === 'CHEATING') return 'Possible shortcut request';
  return 'Safety review needed';
};

const getFlagTone = (flag: FlagItem) => {
  const category = getFlagUiCategory(flag);
  const toneMap: Record<UiFlagCategory, { badge: string; badgeClass: string; accentClass: string }> = {
    Cheating: {
      badge: 'Cheating',
      badgeClass: 'border-transparent bg-rose-600 text-white hover:bg-rose-600 dark:bg-rose-500 dark:text-white dark:hover:bg-rose-500',
      accentClass: 'border-rose-200 bg-rose-50/80 dark:border-rose-900/40 dark:bg-rose-950/20',
    },
    Safety: {
      badge: 'Safety',
      badgeClass: 'border-transparent bg-amber-500 text-slate-950 hover:bg-amber-500 dark:bg-amber-400 dark:text-slate-950 dark:hover:bg-amber-400',
      accentClass: 'border-amber-200 bg-amber-50/80 dark:border-amber-900/40 dark:bg-amber-950/20',
    },
    'Mental Health': {
      badge: 'Mental Health',
      badgeClass: 'border-transparent bg-fuchsia-500 text-white hover:bg-fuchsia-500 dark:bg-fuchsia-500 dark:text-white dark:hover:bg-fuchsia-500',
      accentClass: 'border-fuchsia-200 bg-fuchsia-50/80 dark:border-fuchsia-900/40 dark:bg-fuchsia-950/20',
    },
    'Explicit Content': {
      badge: 'Explicit Content',
      badgeClass: 'border-transparent bg-orange-500 text-white hover:bg-orange-500 dark:bg-orange-500 dark:text-white dark:hover:bg-orange-500',
      accentClass: 'border-orange-200 bg-orange-50/80 dark:border-orange-900/40 dark:bg-orange-950/20',
    },
  };
  return toneMap[category];
};

const getFlagSummary = (flag: FlagItem) => {
  const intent = flag.metadata?.predictedIntent?.intent;
  const risk = flag.metadata?.predictedCheating?.cheatingRisk;
  const type = flag.metadata?.predictedCheating?.requestType;
  const mode = flag.metadata?.routerDecision?.responseMode;
  const reason = flag.reason;
  return [
    reason ? { label: 'Review reason', value: REASON_LABELS[reason] ?? formatLabel(reason) } : null,
    intent ? { label: 'Detected intent', value: INTENT_LABELS[intent] ?? formatLabel(intent) } : null,
    risk ? { label: 'Risk level', value: formatLabel(risk) } : null,
    type ? { label: 'Request type', value: CHEATING_TYPE_LABELS[type] ?? formatLabel(type) } : null,
    mode ? { label: 'Response mode', value: RESPONSE_MODE_LABELS[mode] ?? formatLabel(mode) } : null,
  ].filter(Boolean) as Array<{ label: string; value: string }>;
};

const getQueueTitle = (status: 'PENDING' | 'APPROVED' | 'REJECTED') => {
  if (status === 'APPROVED') return 'Approved flags';
  if (status === 'REJECTED') return 'Rejected flags';
  return 'Review queue';
};

const getReviewBaseline = (
  flag: Pick<
    FlagItem,
    | 'metadata'
    | 'type'
    | 'finalSafetyLabel'
    | 'parentSafetyLabel'
    | 'systemSafetyLabel'
    | 'finalSafetyType'
    | 'parentSafetyType'
    | 'systemSafetyType'
    | 'finalCategory'
    | 'modCategories'
  >,
) => ({
  reviewedIntent: flag.metadata?.reviewedIntent ?? flag.metadata?.predictedIntent?.intent ?? '',
  reviewedCheatingRisk: flag.metadata?.reviewedCheating?.risk ?? flag.metadata?.predictedCheating?.cheatingRisk ?? '',
  reviewedCheatingType: flag.metadata?.reviewedCheating?.type ?? flag.metadata?.predictedCheating?.requestType ?? '',
  reviewedResponseMode: flag.metadata?.reviewedResponseMode ?? flag.metadata?.routerDecision?.responseMode ?? '',
  reviewNote: flag.metadata?.reviewedNote ?? '',
  finalSafetyLabel:
    flag.type === 'MODERATION'
      ? flag.finalSafetyLabel ?? flag.parentSafetyLabel ?? flag.systemSafetyLabel ?? 'SAFETY'
      : 'SAFETY',
  finalSafetyType:
    flag.type === 'MODERATION'
      ? flag.finalSafetyType ?? flag.parentSafetyType ?? flag.systemSafetyType ?? 'GENERAL_SAFETY'
      : 'GENERAL_SAFETY',
  finalCategoryKey:
    flag.type === 'MODERATION'
      ? Object.entries(flag.finalCategory ?? flag.modCategories ?? {}).find(([, enabled]) => Boolean(enabled))?.[0] ?? ''
      : '',
});

export default function FlagsRoute(): JSX.Element {
  const [flags, setFlags] = useState<FlagItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<'MODERATION' | 'CHEATING' | 'ALL'>('ALL');
  const [filterStatus, setFilterStatus] = useState<'PENDING' | 'APPROVED' | 'REJECTED'>('PENDING');
  const [searchTerm, setSearchTerm] = useState('');
  const [pageOffset, setPageOffset] = useState(0);
  const [pagination, setPagination] = useState({ total: 0, limit: PAGE_SIZE, offset: 0, hasMore: false });
  const [reviewDrafts, setReviewDrafts] = useState<
    Record<
      string,
      {
        reviewedIntent?: string;
        reviewedCheatingRisk?: string;
        reviewedCheatingType?: string;
        reviewedResponseMode?: string;
        reviewNote?: string;
        finalSafetyLabel?: 'SAFETY' | 'NOT_SAFETY';
        finalSafetyType?: 'GENERAL_SAFETY' | 'MENTAL_HEALTH' | 'EXPLICIT_CONTENT';
        finalCategoryKey?: string;
      }
    >
  >({});
  const toast = useToast();
  const location = useLocation();
  const navigate = useNavigate();

  const fetchAndSetFlags = async (
    type: 'MODERATION' | 'CHEATING' | 'ALL' = filterType,
    status: 'PENDING' | 'APPROVED' | 'REJECTED' = filterStatus,
    offset: number = pageOffset,
    query: string = searchTerm,
  ) => {
    try {
      setLoading(true);
      const params: {
        type?: 'MODERATION' | 'CHEATING';
        status?: 'PENDING' | 'APPROVED' | 'REJECTED';
        limit?: number;
        offset?: number;
        search?: string;
      } = { limit: PAGE_SIZE, offset };
      if (type !== 'ALL') params.type = type;
      params.status = status;
      if (query.trim()) params.search = query.trim();
      const data = await fetchFlags(params);
      setFlags(
        data.items.map((f: any) => {
          const rawTags =
            Array.isArray(f.tags) ? f.tags : typeof f.tags === 'string' ? JSON.parse(f.tags || '[]') : null;
          const modCategories =
            typeof f.modCategories === 'string' ? JSON.parse(f.modCategories || '{}') : f.modCategories ?? null;
          const metadata =
            typeof f.metadata === 'string' ? JSON.parse(f.metadata || '{}') : f.metadata ?? null;
          const mapped = {
            id: f.id ?? f.flagId,
            parentId: f.parentId ?? f.userId ?? 'Unknown parent',
            childId: f.childId ?? 'Unknown child',
            sessionId: f.sessionId ?? null,
            messageId: f.messageId ?? null,
            text: f.text ?? f.content ?? f.prompt ?? f.body ?? '',
            reason: f.reason ?? f.flagReason ?? null,
            tags: Array.isArray(rawTags) ? rawTags : null,
            parentLabel: f.parentLabel ?? null,
            systemLabel: f.systemLabel ?? null,
            finalLabel: f.finalLabel ?? null,
            parentSafetyLabel: f.parentSafetyLabel ?? null,
            systemSafetyLabel: f.systemSafetyLabel ?? null,
            finalSafetyLabel: f.finalSafetyLabel ?? null,
            parentSafetyType: f.parentSafetyType ?? null,
            systemSafetyType: f.systemSafetyType ?? null,
            finalSafetyType: f.finalSafetyType ?? null,
            modCategories,
            finalCategory: typeof f.finalCategory === 'string' ? JSON.parse(f.finalCategory || '{}') : f.finalCategory ?? null,
            metadata,
            type: f.derivedType ?? deriveFlagType({ ...f, tags: rawTags, modCategories }),
            status: f.reviewStatus ?? f.status,
            createdAt: f.createdAt,
          };
          return mapped;
        })
      );
      setPagination(data.pagination);
      setReviewDrafts(
        Object.fromEntries(
          data.items.map((f: any) => {
            const metadata = typeof f.metadata === 'string' ? JSON.parse(f.metadata || '{}') : f.metadata ?? {};
            return [
              f.id ?? f.flagId,
              getReviewBaseline({
                metadata,
                type: (f.derivedType ?? deriveFlagType({ ...f, tags: Array.isArray(f.tags) ? f.tags : null, modCategories: typeof f.modCategories === 'string' ? JSON.parse(f.modCategories || '{}') : f.modCategories ?? null })) as 'MODERATION' | 'CHEATING',
                finalSafetyLabel: f.finalSafetyLabel ?? null,
                finalSafetyType: f.finalSafetyType ?? null,
                parentSafetyLabel: f.parentSafetyLabel ?? null,
                parentSafetyType: f.parentSafetyType ?? null,
                systemSafetyLabel: f.systemSafetyLabel ?? null,
                systemSafetyType: f.systemSafetyType ?? null,
                finalCategory: typeof f.finalCategory === 'string' ? JSON.parse(f.finalCategory || '{}') : f.finalCategory ?? null,
                modCategories: typeof f.modCategories === 'string' ? JSON.parse(f.modCategories || '{}') : f.modCategories ?? null,
              }),
            ];
          }),
        ),
      );
      setError(null);
    } catch (err: any) {
      setError('Failed to fetch flags.');
      toast.push({
        title: 'Error',
        description: err.response?.data?.message || 'Failed to fetch flags.',
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAndSetFlags(filterType, filterStatus, pageOffset, searchTerm);
  }, [filterType, filterStatus, pageOffset, searchTerm]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const query = params.get('q') ?? '';
    setSearchTerm(query);
  }, [location.search]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (searchTerm) {
      params.set('q', searchTerm);
    } else {
      params.delete('q');
    }
    const nextSearch = params.toString() ? `?${params.toString()}` : '';
    if (nextSearch !== location.search) {
      navigate(`${location.pathname}${nextSearch}`, { replace: true });
    }
  }, [searchTerm, navigate, location.pathname, location.search]);

  useEffect(() => {
    setPageOffset(0);
  }, [filterType, filterStatus, searchTerm]);

  const handleReviewFlag = async (
    flag: FlagItem,
    reviewStatus: 'APPROVED' | 'REJECTED',
    finalLabel?: 'CHEATING' | 'NOT_CHEATING',
    fallbackSafetyLabel?: 'SAFETY' | 'NOT_SAFETY',
  ) => {
    try {
      const draft = reviewDrafts[flag.id] ?? {};
      const baseline = getReviewBaseline(flag);
      const effectiveSafetyLabel =
        flag.type === 'MODERATION' ? draft.finalSafetyLabel ?? fallbackSafetyLabel ?? baseline.finalSafetyLabel : undefined;
      const effectiveSafetyType =
        flag.type === 'MODERATION' ? draft.finalSafetyType ?? baseline.finalSafetyType : undefined;
      const effectiveFinalCategory =
        flag.type === 'MODERATION' && effectiveSafetyLabel === 'SAFETY' && draft.finalCategoryKey
          ? { [draft.finalCategoryKey]: true }
          : effectiveSafetyLabel === 'NOT_SAFETY'
            ? null
            : undefined;
      const hasMeaningfulChange =
        (draft.reviewedIntent ?? '') !== baseline.reviewedIntent ||
        (draft.reviewedCheatingRisk ?? '') !== baseline.reviewedCheatingRisk ||
        (draft.reviewedCheatingType ?? '') !== baseline.reviewedCheatingType ||
        (draft.reviewedResponseMode ?? '') !== baseline.reviewedResponseMode ||
        (flag.type === 'MODERATION' &&
          ((draft.finalSafetyLabel ?? baseline.finalSafetyLabel) !== baseline.finalSafetyLabel ||
            (draft.finalSafetyType ?? baseline.finalSafetyType) !== baseline.finalSafetyType ||
            (draft.finalCategoryKey ?? baseline.finalCategoryKey) !== baseline.finalCategoryKey));
      const reviewNote = (draft.reviewNote ?? '').trim();

      if (reviewStatus === 'REJECTED' && !hasMeaningfulChange && !reviewNote) {
        toast.push({
          title: 'Review details required',
          description: 'Rejecting a flag requires either a corrected review value or a short reviewer note.',
          variant: 'error',
        });
        return;
      }

      if (reviewStatus === 'REJECTED' && reviewNote && !hasMeaningfulChange) {
        const confirmed = window.confirm(
          'Reject this flag using only the reviewer note? No classification values were changed.'
        );
        if (!confirmed) return;
      }

      const reviewedShouldRefuse =
        draft.reviewedResponseMode === 'coach_refusal' || draft.reviewedResponseMode === 'refuse_and_escalate';
      await reviewFlag(flag.id, {
        reviewStatus,
        finalLabel,
        finalSafetyLabel: effectiveSafetyLabel,
        finalSafetyType: effectiveSafetyType,
        finalCategory: effectiveFinalCategory,
        reviewedShouldRefuse,
        reviewedIntent: draft.reviewedIntent || undefined,
        reviewedCheatingRisk: draft.reviewedCheatingRisk || undefined,
        reviewedCheatingType: draft.reviewedCheatingType || undefined,
        reviewedResponseMode: draft.reviewedResponseMode || undefined,
        reviewNote: reviewNote || undefined,
      });
      toast.push({
        title: 'Success',
        description: `Flag ${reviewStatus.toLowerCase()}.`,
        variant: 'success',
      });
      fetchAndSetFlags(filterType, filterStatus, pageOffset, searchTerm);
    } catch (err: any) {
      toast.push({
        title: 'Error',
        description: err.response?.data?.message || `Failed to ${reviewStatus.toLowerCase()} flag.`,
        variant: 'error',
      });
    }
  };

  const visibleFlags = flags;
  const summary = useMemo(
    () =>
      visibleFlags.reduce(
        (acc, flag) => {
          acc.total += 1;
          acc[flag.type] += 1;
          acc[flag.status] += 1;
          return acc;
        },
        { total: 0, MODERATION: 0, CHEATING: 0, PENDING: 0, APPROVED: 0, REJECTED: 0 }
      ),
    [visibleFlags]
  );

  const currentPage = Math.floor(pageOffset / pagination.limit) + 1;
  const totalPages = Math.max(1, Math.ceil(pagination.total / pagination.limit));

  const updateDraft = (
    flagId: string,
    field: 'reviewedIntent' | 'reviewedCheatingRisk' | 'reviewedCheatingType' | 'reviewedResponseMode' | 'reviewNote',
    value: string,
  ) => {
    setReviewDrafts((current) => ({
      ...current,
      [flagId]: {
        ...(current[flagId] ?? {}),
        [field]: value,
      },
    }));
  };

  const updateModerationDraft = (
    flagId: string,
    field: 'finalSafetyLabel' | 'finalSafetyType' | 'finalCategoryKey',
    value: string,
  ) => {
    setReviewDrafts((current) => ({
      ...current,
      [flagId]: {
        ...(current[flagId] ?? {}),
        [field]: value,
      },
    }));
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Header />
        <Card>
          <CardContent className="py-16 text-center text-muted-foreground">Loading flags…</CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <Header />
        <Card>
          <CardContent className="py-16 text-center text-destructive">{error}</CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Header />

      <section className="rounded-[32px] border border-white/60 bg-gradient-to-br from-white/90 to-primary/5 p-6 shadow-[0_25px_60px_-40px_rgba(15,23,42,1)] dark:border-slate-700/40 dark:bg-slate-900/80">
        <div className="grid gap-4 md:grid-cols-4">
          <SummaryCard label="Open reviews" value={summary.PENDING} />
          <SummaryCard label="Cheating" value={summary.CHEATING} />
          <SummaryCard label="Safety" value={summary.MODERATION} />
          <SummaryCard label="Reviewed" value={summary.APPROVED + summary.REJECTED} />
        </div>
        <div className="mt-6 flex flex-wrap items-center gap-3">
          {(['PENDING', 'APPROVED', 'REJECTED'] as const).map((status) => (
            <Button
              key={status}
              variant="ghost"
              className={cn(
                'rounded-full border px-4 text-sm font-semibold',
                filterStatus === status
                  ? 'border-primary bg-primary/10 text-primary dark:border-transparent dark:bg-blue-600 dark:text-white'
                  : 'border-transparent text-slate-600 hover:border-slate-200 dark:text-slate-200 dark:hover:bg-slate-800/80'
              )}
              onClick={() => setFilterStatus(status)}
            >
              {status.charAt(0) + status.slice(1).toLowerCase()}
            </Button>
          ))}
          <Select value={filterType} onValueChange={(value: 'MODERATION' | 'CHEATING' | 'ALL') => setFilterType(value)}>
            <SelectTrigger className="w-44 rounded-full border-slate-200 dark:border-slate-600 bg-white/80 dark:bg-slate-900/80 text-sm dark:text-slate-100">
              <SelectValue placeholder="All types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All reviews</SelectItem>
              <SelectItem value="MODERATION">Safety reviews</SelectItem>
              <SelectItem value="CHEATING">Cheating reviews</SelectItem>
            </SelectContent>
          </Select>
          <Input
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search prompt or user ID"
            className="h-10 w-full rounded-full border-slate-200 bg-white/80 px-4 text-sm md:w-64 dark:border-slate-600 dark:bg-slate-900/80"
          />
          <Button
            variant="ghost"
            className="rounded-full text-sm text-slate-500 dark:text-slate-200 dark:hover:bg-slate-800/80"
            onClick={() => {
              setFilterType('ALL');
              setFilterStatus('PENDING');
              setSearchTerm('');
            }}
          >
            <RefreshCcw className="mr-2 h-4 w-4" />
            Reset
          </Button>
        </div>
      </section>

      <div className="space-y-6">
        <Alert className="border-slate-200/80 bg-white/80 dark:border-slate-700/50 dark:bg-slate-900/70">
          <Flag className="h-4 w-4" />
          <AlertTitle>Review each flag as a moderation decision</AlertTitle>
          <AlertDescription>
            The system labels below are translated into plain language. Only the review controls use the raw policy values.
          </AlertDescription>
        </Alert>

        <Card className="border border-slate-100 dark:border-slate-700/40 bg-white/95 dark:bg-slate-900/80">
          <CardHeader>
            <CardTitle>{getQueueTitle(filterStatus)}</CardTitle>
            <CardDescription>
              {visibleFlags.length
                ? filterStatus === 'PENDING'
                  ? 'Review open items, update the classification, then approve or reject.'
                  : `Showing ${filterStatus.toLowerCase()} items.`
                : `No ${filterStatus.toLowerCase()} flags.`}
              {pagination.total > 0 ? ` ${pagination.total} total match the current filters.` : ''}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {visibleFlags.length > 0 ? (
              <div className="space-y-4">
                {visibleFlags.map((flag) => {
                  const moderationTags = getFlagModerationSubcategoryLabels(flag);
                  const tone = getFlagTone(flag);
                  const summaryItems = getFlagSummary(flag);
                  return (
                    <div
                      key={flag.id}
                      className={cn(
                        'rounded-[28px] border p-5 shadow-sm transition-colors',
                        'border-slate-200/80 bg-white dark:border-slate-700/50 dark:bg-slate-950/60',
                        tone.accentClass
                      )}
                    >
                      <div className="flex flex-wrap items-start justify-between gap-4">
                        <div className="min-w-0 flex-1 space-y-4">
                          <div className="flex flex-wrap items-center gap-2">
                            <Badge className={cn('rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em]', tone.badgeClass)}>
                              {tone.badge}
                            </Badge>
                            <Badge className="rounded-full border-transparent bg-slate-100 px-3 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-100 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-800">
                              {flag.status === 'PENDING' ? 'Awaiting review' : formatLabel(flag.status)}
                            </Badge>
                            <span className="text-xs text-slate-500 dark:text-slate-300">
                              {new Date(flag.createdAt).toLocaleString()}
                            </span>
                          </div>

                          <div className="space-y-2">
                            <p className="text-xl font-semibold text-slate-950 dark:text-slate-50">{getFlagHeadline(flag)}</p>
                            <p className="max-w-4xl text-base leading-8 text-slate-700 dark:text-slate-200">
                              {flag.text || 'No prompt captured.'}
                            </p>
                          </div>

                          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                            {summaryItems.map((item) => (
                              <div key={item.label} className="rounded-2xl border border-slate-200/80 bg-white/85 px-4 py-3 dark:border-slate-700/50 dark:bg-slate-900/80">
                                <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-500 dark:text-slate-300">
                                  {item.label}
                                </p>
                                <p className="mt-1 text-sm font-medium text-slate-900 dark:text-slate-100">{item.value}</p>
                              </div>
                            ))}
                          </div>

                          {moderationTags.length ? (
                            <div className="flex flex-wrap gap-2">
                              {moderationTags.map((label) => (
                                <Badge
                                  key={label}
                                  className="rounded-full border-transparent bg-slate-200 text-slate-900 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-100 dark:hover:bg-slate-700"
                                >
                                  {label}
                                </Badge>
                              ))}
                            </div>
                          ) : null}

                          <div className="grid gap-3 pt-2 md:grid-cols-2 xl:grid-cols-4">
                            {flag.type === 'MODERATION' ? (
                              <>
                                <ReviewSelect
                                  label="Safety family"
                                  value={reviewDrafts[flag.id]?.finalSafetyType || 'GENERAL_SAFETY'}
                                  onValueChange={(value) => {
                                    updateModerationDraft(flag.id, 'finalSafetyType', value);
                                    if (value === 'MENTAL_HEALTH') {
                                      updateModerationDraft(flag.id, 'finalCategoryKey', 'self-harm');
                                    } else if (value === 'EXPLICIT_CONTENT') {
                                      updateModerationDraft(flag.id, 'finalCategoryKey', 'sexual');
                                    }
                                  }}
                                >
                                  {SAFETY_TYPE_OPTIONS.map((option) => (
                                    <SelectItem key={option.value} value={option.value}>
                                      {option.label}
                                    </SelectItem>
                                  ))}
                                </ReviewSelect>
                                <ReviewSelect
                                  label="Safety verdict"
                                  value={reviewDrafts[flag.id]?.finalSafetyLabel || 'SAFETY'}
                                  onValueChange={(value) => {
                                    updateModerationDraft(flag.id, 'finalSafetyLabel', value);
                                    if (value === 'NOT_SAFETY') {
                                      updateModerationDraft(flag.id, 'finalCategoryKey', '');
                                    }
                                  }}
                                >
                                  <SelectItem value="SAFETY">Safety issue</SelectItem>
                                  <SelectItem value="NOT_SAFETY">No safety issue</SelectItem>
                                </ReviewSelect>
                                <ReviewSelect
                                  label="Safety category"
                                  value={
                                    reviewDrafts[flag.id]?.finalSafetyLabel === 'NOT_SAFETY'
                                      ? 'UNSET'
                                      : reviewDrafts[flag.id]?.finalCategoryKey || 'UNSET'
                                  }
                                  onValueChange={(value) =>
                                    updateModerationDraft(flag.id, 'finalCategoryKey', value === 'UNSET' ? '' : value)
                                  }
                                >
                                  <SelectItem value="UNSET">Keep detected category</SelectItem>
                                  {MODERATION_CATEGORY_OPTIONS.filter((option) => {
                                    const family = reviewDrafts[flag.id]?.finalSafetyType || 'GENERAL_SAFETY';
                                    if (family === 'MENTAL_HEALTH') return option.key === 'self-harm';
                                    if (family === 'EXPLICIT_CONTENT') return option.key === 'sexual';
                                    return option.key !== 'self-harm' && option.key !== 'sexual';
                                  }).map((option) => (
                                    <SelectItem key={option.key} value={option.key}>
                                      {option.label}
                                    </SelectItem>
                                  ))}
                                </ReviewSelect>
                              </>
                            ) : null}
                            <ReviewSelect
                              label="Reviewed intent"
                              value={reviewDrafts[flag.id]?.reviewedIntent || 'UNSET'}
                              onValueChange={(value) => updateDraft(flag.id, 'reviewedIntent', value === 'UNSET' ? '' : value)}
                            >
                              <SelectItem value="UNSET">Keep detected intent</SelectItem>
                              <SelectItem value="factual_definition">Factual question</SelectItem>
                              <SelectItem value="learning_help">Learning help</SelectItem>
                              <SelectItem value="assignment_completion">Assignment shortcut</SelectItem>
                            </ReviewSelect>
                            <ReviewSelect
                              label="Risk level"
                              value={reviewDrafts[flag.id]?.reviewedCheatingRisk || 'UNSET'}
                              onValueChange={(value) => updateDraft(flag.id, 'reviewedCheatingRisk', value === 'UNSET' ? '' : value)}
                            >
                              <SelectItem value="UNSET">Keep detected risk</SelectItem>
                              <SelectItem value="LOW">Low</SelectItem>
                              <SelectItem value="MEDIUM">Medium</SelectItem>
                              <SelectItem value="HIGH">High</SelectItem>
                            </ReviewSelect>
                            <ReviewSelect
                              label="Request type"
                              value={reviewDrafts[flag.id]?.reviewedCheatingType || 'UNSET'}
                              onValueChange={(value) => updateDraft(flag.id, 'reviewedCheatingType', value === 'UNSET' ? '' : value)}
                            >
                              <SelectItem value="UNSET">Keep detected type</SelectItem>
                              <SelectItem value="FACTUAL">Factual question</SelectItem>
                              <SelectItem value="LEARNING_HELP">Learning help</SelectItem>
                              <SelectItem value="CHECK_WORK">Check work</SelectItem>
                              <SelectItem value="SINGLE_PROBLEM_HELP">Single problem help</SelectItem>
                              <SelectItem value="BULK_WORKSHEET">Worksheet answers</SelectItem>
                              <SelectItem value="ANSWERS_ONLY">Answers only</SelectItem>
                              <SelectItem value="ESSAY_WRITE">Write the essay</SelectItem>
                              <SelectItem value="OTHER">Other</SelectItem>
                            </ReviewSelect>
                            <ReviewSelect
                              label="Response mode"
                              value={reviewDrafts[flag.id]?.reviewedResponseMode || 'UNSET'}
                              onValueChange={(value) => updateDraft(flag.id, 'reviewedResponseMode', value === 'UNSET' ? '' : value)}
                            >
                              <SelectItem value="UNSET">Keep detected mode</SelectItem>
                              <SelectItem value="direct_answer">Direct answer</SelectItem>
                              <SelectItem value="teach_with_example">Teach with example</SelectItem>
                              <SelectItem value="check_work_only">Check work only</SelectItem>
                              <SelectItem value="coach_refusal">Refuse shortcut and coach</SelectItem>
                              <SelectItem value="guided_answer">Safety-limited answer</SelectItem>
                              <SelectItem value="safety_redirect">Support and escalate</SelectItem>
                              <SelectItem value="refuse_and_escalate">Block and redirect</SelectItem>
                            </ReviewSelect>
                          </div>

                          <div className="space-y-2">
                            <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-300">
                              Reviewer note
                            </Label>
                            <Textarea
                              value={reviewDrafts[flag.id]?.reviewNote ?? ''}
                              onChange={(event) => updateDraft(flag.id, 'reviewNote', event.target.value)}
                              placeholder="Add why you are rejecting or what should change."
                              className="min-h-[96px] rounded-2xl border-slate-200 bg-white/90 text-sm dark:border-slate-700 dark:bg-slate-950"
                            />
                          </div>
                        </div>

                        <div className="flex shrink-0 items-center gap-2">
                          {flag.status === 'PENDING' ? (
                            <>
                              <Button
                                size="sm"
                                className="rounded-full bg-emerald-600 px-4 text-xs font-semibold text-white hover:bg-emerald-500"
                                onClick={() =>
                                  handleReviewFlag(
                                    flag,
                                    'APPROVED',
                                    flag.type === 'CHEATING' ? 'CHEATING' : undefined,
                                    flag.type === 'MODERATION' ? 'SAFETY' : undefined,
                                  )
                                }
                              >
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                className="rounded-full bg-rose-600 px-4 text-xs font-semibold text-white hover:bg-rose-500"
                                onClick={() =>
                                  handleReviewFlag(
                                    flag,
                                    'REJECTED',
                                    flag.type === 'CHEATING' ? 'NOT_CHEATING' : undefined,
                                    flag.type === 'MODERATION' ? 'NOT_SAFETY' : undefined,
                                  )
                                }
                              >
                                Reject
                              </Button>
                            </>
                          ) : (
                            <Badge className="rounded-full border-transparent bg-slate-100 px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-100 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-800">
                              {flag.status === 'APPROVED' ? 'Approved' : 'Rejected'}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-center text-sm text-slate-500 dark:text-slate-300">
                {`No ${filterStatus.toLowerCase()} flags match the current filters.`}
              </p>
            )}

            {pagination.total > pagination.limit ? (
              <div className="flex flex-col gap-3 border-t border-slate-200/80 pt-4 dark:border-slate-700/50 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-slate-500 dark:text-slate-300">
                  Showing {pagination.offset + 1}-{Math.min(pagination.offset + visibleFlags.length, pagination.total)} of {pagination.total}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    className="rounded-full"
                    disabled={pageOffset === 0 || loading}
                    onClick={() => setPageOffset((current) => Math.max(current - pagination.limit, 0))}
                  >
                    Previous
                  </Button>
                  <span className="min-w-24 text-center text-sm text-slate-600 dark:text-slate-300">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="ghost"
                    className="rounded-full"
                    disabled={!pagination.hasMore || loading}
                    onClick={() => setPageOffset((current) => current + pagination.limit)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            ) : null}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function Header() {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 text-slate-700 dark:text-slate-100">
      <div>
        <p className="text-sm font-semibold uppercase tracking-[0.25em] text-primary/70">Review</p>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Flags</h1>
        <p className="text-sm text-slate-500 dark:text-slate-300">Cheating and moderation flags pending admin review.</p>
      </div>
    </div>
  );
}

function SummaryCard({ label, value }: { label: string; value: number }) {
  return (
    <Card className="border border-slate-100 bg-white/90 shadow-sm dark:border-slate-700/40 dark:bg-slate-900/80">
      <CardContent className="px-5 py-4">
        <p className="text-xs uppercase tracking-[0.3em] text-slate-400 dark:text-slate-300">{label}</p>
        <p className="text-2xl font-semibold text-slate-900 dark:text-slate-100">{value}</p>
      </CardContent>
    </Card>
  );
}

function ReviewSelect({
  label,
  value,
  onValueChange,
  children,
}: {
  label: string;
  value: string;
  onValueChange: (value: string) => void;
  children: ReactNode;
}) {
  return (
    <div className="space-y-2">
      <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-300">{label}</Label>
      <Select value={value} onValueChange={onValueChange}>
        <SelectTrigger className="h-10 rounded-2xl border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-950">
          <SelectValue placeholder={label} />
        </SelectTrigger>
        <SelectContent>{children}</SelectContent>
      </Select>
    </div>
  );
}
